import React, { createContext, useContext, useCallback, useMemo } from "react";
import { WebAuth } from "auth0-js";
import { reportPortalAuthError } from "./portal_auth_errors";
import { portalAuth } from "./portal_auth_config";
import { useConfig } from "@/core/config";
import { portalAuthPaths } from "./portal_paths";
import { withPartnerSlug } from "./portal_paths";

interface AuthContextV2 {
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  logout: () => void;
}

const AuthContextV2 = createContext<AuthContextV2 | null>(null);

interface AuthPortalCustomLoginProviderProps {
  children: React.ReactNode;
  partnerSlug: string | undefined;
}

export function useWebAuth(partnerSlug: string | undefined) {
  const portalConfig = portalAuth(partnerSlug);
  const config = useConfig();
  const currentPortalAuthConfig = portalConfig[config.environment];
  return useMemo(
    () =>
      new WebAuth({
        clientID: currentPortalAuthConfig.clientId,
        domain: currentPortalAuthConfig.domain,
        audience: currentPortalAuthConfig.audience,
        redirectUri: currentPortalAuthConfig.redirectUri,
        responseType: "token",
        scope: "openid profile email",
      }),
    [
      currentPortalAuthConfig.audience,
      currentPortalAuthConfig.clientId,
      currentPortalAuthConfig.domain,
      currentPortalAuthConfig.redirectUri,
    ]
  );
}

export function AuthPortalCustomLoginProvider({
  children,
  partnerSlug,
}: AuthPortalCustomLoginProviderProps) {
  const webAuth = useWebAuth(partnerSlug);

  const login = useCallback(
    async (email: string, password: string) => {
      return new Promise<void>((resolve, reject) => {
        webAuth.login(
          {
            email,
            password,
            realm: "Username-Password-Authentication",
          },
          (error) => {
            if (error) {
              reportPortalAuthError(error, "login");
              reject(error);
              return;
            }
            resolve();
          }
        );
      });
    },
    [webAuth]
  );

  const signup = useCallback(
    async (email: string, password: string) => {
      return new Promise<void>((resolve, reject) => {
        webAuth.signup(
          {
            email,
            password,
            connection: "Username-Password-Authentication",
          },
          (error) => {
            if (error) {
              reportPortalAuthError(error, "signup");
              reject(error);
              return;
            }
            // After successful signup, automatically log the user in
            webAuth.login(
              {
                email,
                password,
                realm: "Username-Password-Authentication",
              },
              (loginError) => {
                if (loginError) {
                  reportPortalAuthError(loginError, "login");
                  reject(loginError);
                  return;
                }
                resolve();
              }
            );
          }
        );
      });
    },
    [webAuth]
  );

  const logout = useCallback(() => {
    webAuth.logout({
      returnTo: `${window.location.origin}${withPartnerSlug(
        portalAuthPaths.login,
        partnerSlug
      )}`,
    });
  }, [webAuth, partnerSlug]);

  const forgotPassword = useCallback(
    async (email: string) => {
      return new Promise<void>((resolve, reject) => {
        webAuth.changePassword(
          {
            email,
            connection: "Username-Password-Authentication",
          },
          (error) => {
            if (error) {
              reportPortalAuthError(error, "forgotPassword");
              reject(error);
              return;
            }
            resolve();
          }
        );
      });
    },
    [webAuth]
  );

  const value = useMemo(
    () => ({
      login,
      signup,
      forgotPassword,
      logout,
    }),
    [login, signup, forgotPassword, logout]
  );

  return (
    <AuthContextV2.Provider value={value}>{children}</AuthContextV2.Provider>
  );
}

export function useAuthV2() {
  const context = useContext(AuthContextV2);
  if (!context) {
    throw new Error("useAuthV2 must be used within an AuthPortalProviderV2");
  }
  return context;
}
